import {
    Avatar,
    Box,
    Button,
    ButtonGroup,
    Flex,
    Grid,
    GridItem,
    Heading,
    IconButton,
    List,
    ListIcon,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Spacer,
    Spinner,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    Wrap,
    WrapItem,
    useDisclosure
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdAdd } from "react-icons/md";
import { useHistory } from "react-router";
import CallItem from "./call-item";
import Participants from "./participants";
import { Container, IncommingCallContainer } from "./style";
import { GetCallStatus } from "./utils";
import { VideoConference } from "./video-conference";
import WaitingCallItem from "./waiting-call-item";
import { AccountAvatar } from "../../components/AccountAvatar";
import JitsiMeet from "../../components/JitsiMeet";
import { LoadMore } from "../../components/LoadMore";
import { Title } from "../../components/Title";
import {
    AccountRole,
    Call,
    IncomingCallSubscription,
    MyCallsDocument,
    ParticipantRole,
    useAccountsQuery,
    useAnswerCallMutation,
    useInviteMutation,
    useMyWaitingQueueSubscription,
    useTerminateCallMutation
} from "../../graphql";
import {
    useAuthToken,
    useIncomingCall,
    useMyCalls,
    useRinging
} from "../../hooks";
import {
    ShortAccount,
    formatDate,
    formatDateTime,
    getFullName
} from "../../utils";
import { AllCallsFilter, DatesFilter } from "../Call/filter";

export function IncomingCallAnswerModal() {
    const { t } = useTranslation();
    const history = useHistory();
    const { data, ringing } = useIncomingCall();

    const call = data?.incomingCall?.call;

    const caller = call?.participants?.find(
    (p) => p.role === ParticipantRole.Caller
  );

    const [answerCall, { loading: answeringInFLight }] = useAnswerCallMutation();

    const [
        terminateCall,
        { loading: terminatingInFLight }
    ] = useTerminateCallMutation();

    if (!ringing) {
        return null;
    }

    const onAnswer = () => {
        history.push("/calls/my");
        answerCall({
            variables: { id: call?.id }
        });
    };

    const onLeave = () => {
        terminateCall({
            variables: { id: call?.id }
        });
    };

    const callerFullName = getFullName(caller.account as ShortAccount);
    return (
        <>
            <Modal isOpen={ ringing } onClose={ () => false }>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        { t("calls.calling", { name: callerFullName }) }
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <IncommingCallContainer>
                            <Avatar size="xl" name={ callerFullName } src={ callerFullName } />
                        </IncommingCallContainer>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            colorScheme="red"
                            mr={ 3 }
                            onClick={ onLeave }
                            isLoading={ terminatingInFLight }
                        >
                            { t("calls.close") }
                        </Button>

                        <Button
                            onClick={ onAnswer }
                            colorScheme="teal"
                            isLoading={ answeringInFLight }
                        >
                            { t("calls.answer") }
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export const CallLog: React.FC = () => {
    const { t } = useTranslation();
    const {
        loading,
        items,
        loadMore,
        hasNextPage,
        totalCount,
        refresh,
        init
    } = useMyCalls();

    return (
        <>
            <Box borderWidth="1px" borderRadius="lg" background="#FFF" p="10px">
                <Title title={ t("calls.title") }>
                    <DatesFilter
                        onChange={ (data) => {
                            refresh(data);
                        } }
                    />
                </Title>

                <Table variant="striped" size="sm" marginTop="15px">
                    <Thead>
                        <Tr>
                            <Th>{ t("calls.caller") }</Th>
                            <Th>{ t("calls.guests") }</Th>
                            <Th>{ t("calls.status") }</Th>
                            <Th>{ t("calls.date") }</Th>
                            <Th>{ t("calls.duration") }</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        { items?.map((call) => {
                            const participants = call.participants;

                            const caller = participants.find(
                                (p) => p.role === ParticipantRole.Caller
                            );
                            const callee = participants.find(
                                (p) => p.role === ParticipantRole.Callee
                            );
                            const guests = participants.filter(
                                (p) => p.role === ParticipantRole.GuestCallee
                            );

                            return (
                                <Tr key={ call.id }>
                                    <Td>
                                        <AccountAvatar
                                            key={ caller?.account?.id }
                                            account={ caller?.account }
                                            size="xs"
                                            role={ false }
                                        />
                                    </Td>
                                    <Td>
                                        <Stack direction="row">
                                            { guests.length > 0
                                                ? guests.map((participant) => (
                                                    <AccountAvatar
                                                        key={ participant?.account.id }
                                                        account={ participant?.account }
                                                        size="xs"
                                                        role={ false }
                                                    />
                                                ))
                                                : t("calls.none") }
                                        </Stack>
                                    </Td>
                                    <Td>
                                        <GetCallStatus call={ call } />
                                    </Td>
                                    <Td>
                                        { call.requestTime ? formatDate(call.requestTime) : "-" }
                                    </Td>
                                    <Td>
                                        { call.duration } { t("calls.minutes") }
                                    </Td>
                                </Tr>
                            );
                        }) }
                    </Tbody>
                </Table>

                <LoadMore hasNextPage={ hasNextPage } loadMore={ loadMore } />
            </Box>
        </>
    );
};

const CallWaitingQueue: React.FC = ({ children }) => {
    const { t } = useTranslation();
    const { data, loading } = useMyWaitingQueueSubscription();
    const { myWaitingQueue: queue = [] } = data || {};

    return (
        <>
            <List spacing={ 3 }>
                { queue.length ? (
                    <>
                        { queue.map((call) => (
                            <WaitingCallItem call={ call as Call } />
                        )) }
                    </>
                ) : (
                    <Text>{ t("calls.noWaitingCalls") }</Text>
                ) }
            </List>
        </>
    );
};

const CallPage: React.FC = ({ children }) => {
    const { t } = useTranslation();
    const { answering, calling, ringing, data } = useIncomingCall();
    useRinging();

    return (
        <>
            <IncomingCallAnswerModal />
            <Container>
                <Grid templateColumns="repeat(5, 1fr)" gap={ 4 }>
                    <GridItem
                        colSpan={ 4 }
                        paddingRight={ 0 }
                        height="calc(100vh - 80px)"
                        overflow="scroll"
                    >
                        { answering ? <VideoConference data={ data } /> : <CallLog /> }
                    </GridItem>
                    <GridItem colStart={ 5 } colEnd={ 6 }>
                        <Box borderWidth="1px" borderRadius="lg" background="#FFF" p="10px">
                            <Title title={ t("calls.waitingCalls") }></Title>
                            <CallWaitingQueue />
                        </Box>
                    </GridItem>
                </Grid>
            </Container>
        </>
    );
};

export default CallPage;
